import { Inject, Injectable, InjectionToken } from '@angular/core';
import { pushEvent } from '@allianz/tracking';
import { TrackingModuleConfig } from '../model/tracking.model';
import { PersistStorageKeys } from '../services/persist-storage.service';
import { AdobeDTMWindow } from '@allianz/tracking/models/trackify.model';
import { TrackingInitEvent } from './model/init-event.model';
import { TrackingPageChangeEvent } from './model/page-change.model';
import { TrackingPreviousPage } from './model/previous-page.model';
import { TrackingPageErrorEvent } from './model/page-error.model';
import { ProcessType } from '../model/process-type.model';
import { TrackingStep2Event } from './model/step-2.model';
import { TrackingExtensionsEvent } from './model/extensions.model';
import { environment } from '../environments/environment';
import { AppType } from '../model/app-type.model';
import { STEPS_AGENT_CONFIG } from '../config/step-agent.config';
import { addLeadingZero } from '../utils';
import { STEPS_DIRECT_CONFIG_FOR_TRACKING } from '../config/step-direct.config';
import { TranslateService } from '@ngx-translate/core';
import { first, map, Observable } from 'rxjs';
import { TrackingSuccessEvent } from './model/success.model';

declare const window: AdobeDTMWindow;

export const TRACKING_CONFIG = new InjectionToken<TrackingModuleConfig>('TRACKING_DEBUG');

const STEP_TRACKING_DIFF = 1;

@Injectable({
  providedIn: 'root',
})
export class TrackingService {
  private previousPage: TrackingPreviousPage = {};

  constructor(@Inject(TRACKING_CONFIG) private readonly config: TrackingModuleConfig, private readonly translateService: TranslateService) {
    const trackingDebug = localStorage.getItem(PersistStorageKeys.TrackingDebug) === 'true';

    window.trackifyDebug = trackingDebug || config.debug || false;
  }

  public init(details: TrackingInitEvent): void {
    this.sendEvent('page.pageInfo.pageName', details.pageName, true);
    this.sendEvent('page.pageInfo.language', details.language, true);
    this.sendEvent('page.pageInfo.siteSection', details.siteSection, true);
  }

  public setStep(step: number): void {
    switch (environment.app) {
      case AppType.Direct: {
        const stepName = STEPS_DIRECT_CONFIG_FOR_TRACKING[step]?.label ?? 'UnknownStepName';
        const stepNumber = addLeadingZero(step + STEP_TRACKING_DIFF);

        this.pageChange({ stepName, stepNumber });
        break;
      }
      default: {
        const stepName = STEPS_AGENT_CONFIG[step]?.label ?? 'UnknownStepName';
        const stepNumber = addLeadingZero(step + STEP_TRACKING_DIFF);

        this.pageChange({ stepName, stepNumber });
        break;
      }
    }
  }

  public pageError(details: TrackingPageErrorEvent): void {
    this.sendEvent('page.error.type', details.type, true);
    this.sendEvent('page.error.code', details.code, true);
    this.sendEvent('page.error.message', details.message, true);
  }

  public businessEventStart(): void {
    this.sendEvent('process.attributes.processDetails.businessEvent', 'start', true);
  }

  public businessEventEnd(event: TrackingSuccessEvent): void {
    if (event.policyNumber) {
      this.sendEvent('user.policyNumber', event.policyNumber, true);
    }

    if (event.primaryCategory) {
      this.sendEvent('product.productInfo.primaryCategory', event.primaryCategory, true);
    }

    if (event.productName) {
      this.sendEvent('product.name', event.productName, true);
    }

    if (event.productPrice) {
      this.sendEvent('product.price', event.productPrice, true);
    }

    this.sendEvent('process.attributes.processDetails.businessEvent', 'success', true);
  }

  public nextPage(): void {
    this.sendEvent('helptext.navigationButton', 'NEXT_BUTTON', true);
  }

  public setUserType(userType: 'customer' | 'agent'): void {
    this.sendEvent('user.type', userType, true);
  }

  public insuranceSubject(insuranceSubject: ProcessType): void {
    this.sendEvent('process.attributes.processDetails.travelDetails.insuranceSubject', insuranceSubject, true);
  }

  public step2(event: TrackingStep2Event): void {
    if (event.destination !== null) {
      this.sendEvent('process.attributes.processDetails.travelDetails.destination', event.destination, true);
    }

    if (event.departureDate !== null) {
      this.sendEvent('process.attributes.processDetails.travelDetails.departureDate', event.departureDate, true);
    }

    if (event.returnDate !== null) {
      this.sendEvent('process.attributes.processDetails.travelDetails.returnDate', event.returnDate, true);
    }

    if (event.peopleNumber !== null) {
      this.sendEvent('process.attributes.processDetails.travelDetails.peopleNumber', event.peopleNumber, true);
    }

    if (event.allInPoland !== null) {
      this.sendEvent('process.attributes.processDetails.travelDetails.allInPoland', event.allInPoland, true);
    }

    if (event.extremeSports !== null) {
      this.sendEvent('process.attributes.processDetails.travelDetails.extremeSports', event.extremeSports, true);
    }

    if (event.phoneNumber !== null) {
      this.hashValue(event.phoneNumber)
        .then((hash) => {
          this.sendEvent('process.attributes.processDetails.travelDetails.phoneNumber', hash, true);
          this.sendEvent('user.details.mobilePhone', hash, true);
        })
        .catch(() => {
          this.sendEvent('process.attributes.processDetails.travelDetails.phoneNumber', 'hashedError', true);
          this.sendEvent('user.details.mobilePhone', 'hashedError', true);
        });
    }

    if (event.travelersDOB !== null) {
      this.sendEvent('process.attributes.processDetails.travelDetails.travelersDOB', event.travelersDOB, true);
    }

    if (event.extremeSportsEnabled !== null) {
      this.sendEvent('process.attributes.processDetails.travelDetails.extremeSportsEnabled', event.extremeSportsEnabled, true);
    }
  }

  public extensions(event: TrackingExtensionsEvent): void {
    this.sendEvent('process.attributes.processDetails.travelDetails.extension', event, true);
  }

  public promoCode(event: boolean): void {
    this.sendEvent('process.attributes.processDetails.travelDetails.promoCode', event, true);
  }

  public specialOffer(event: string | null): void {
    this.sendEvent('process.attributes.processDetails.travelDetails.specialOffer', event, true);
  }

  public contactAgentOpen(): void {
    this.sendEvent('helptext.navigationButton', 'MEET_AGENT_BUTTON', true);
  }

  public contactAgentSent(): void {
    this.sendEvent('helptext.navigationButton', 'MEET_AGENT_SUCCESS', true);
  }

  public fabOpen(): void {
    this.sendEvent('helptext.navigationButton', 'NEED_HELP', true);
  }

  public productInfoPrimaryCategory(primaryCategory: string | null): void {
    this.sendEvent('product.productInfo.primaryCategory', primaryCategory, true);
  }

  public productName(productName: string | null): void {
    this.sendEvent('product.name', productName, true);
  }

  public productPrice(productPrice: number | null): void {
    this.sendEvent('product.price', productPrice, true);
  }

  public paymentFrequency(paymentFrequency: string): void {
    this.sendEvent('product.paymentFrequency', paymentFrequency, true);
  }

  public more(more: string): void {
    this.sendEvent('helptext.navigationButton', `MORE:${more}`, true);
  }

  public compare(): void {
    this.sendEvent('helptext.navigationButton', `COMPARE`, true);
  }

  public edit(edit: string): void {
    this.sendEvent('helptext.navigationButton', `EDIT:${edit}`, true);
  }

  public downloadDocs(): void {
    this.sendEvent('helptext.navigationButton', `CHECK_DOWNLOAD_DOCS`, true);
  }

  public downloadPolicy(): void {
    this.sendEvent('helptext.navigationButton', `DOWNLOAD_POLICY`, true);
  }

  public openModal(event: string): void {
    this.sendEvent('page.attributes.popup', event, true);
  }

  private pageChange(details: TrackingPageChangeEvent): void {
    this.getTranslation(details.stepName)
      .pipe(first())
      .subscribe((stepNameTranslation) => {
        this.sendEvent('process.stepName', stepNameTranslation, true);
        this.sendEvent('process.stepNumber', details.stepNumber, true);
        if (this.previousPage.stepName) {
          this.sendEvent('process.previousStepName', this.previousPage.stepName, true);
        }

        if (this.previousPage.stepNumber) {
          this.sendEvent('process.previousStepNumber', this.previousPage.stepNumber, true);
        }

        this.sendEvent('process.category', 'Quote&Buy', true);
        this.sendEvent('process.processName', 'Quote & Buy - Travel', true);

        this.previousPage.stepName = stepNameTranslation;
        this.previousPage.stepNumber = details.stepNumber;
      });
  }

  private sendEvent(originalEvent: string, value: any, ignorePageId = false): void {
    pushEvent(originalEvent, value, ignorePageId);
  }

  private async hashValue(value: string): Promise<string> {
    const msgUint8 = new TextEncoder().encode(value);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  private getTranslation(key: string): Observable<string> {
    return this.translateService.get(key).pipe(map((value) => (value ? String(value) : key)));
  }
}
